// =====================================================
// DOCX MARKUP — visual redline styling (strike/underline/color) added to run
// properties. Node-based so injecting markup can't corrupt the run XML.
// =====================================================

import { W_NS, parseEl, makeEl, serializeClean } from './xml-helpers';

interface MarkEl { tag: string; attrs?: Record<string, string>; }

// CT_RPr child order. Run properties are an ordered sequence, so markup elements
// (strike/dstrike/color/u) must be inserted at their schema position — appending
// them puts e.g. <w:color> after <w:sz>/<w:u>, which the OOXML schema rejects
// (Word tolerates it, stricter tools don't).
// CT_ParaRPr puts the paragraph-mark revision markers (ins/del/moveFrom/moveTo) BEFORE
// the base properties; they must sort first, and rPrChange last (unknowns sort last).
const RPR_ORDER = ['ins', 'del', 'moveFrom', 'moveTo', 'rStyle', 'rFonts', 'b', 'bCs', 'i', 'iCs', 'caps', 'smallCaps', 'strike',
  'dstrike', 'outline', 'shadow', 'emboss', 'imprint', 'noProof', 'snapToGrid', 'vanish',
  'webHidden', 'color', 'spacing', 'w', 'kern', 'position', 'sz', 'szCs', 'highlight', 'u',
  'effect', 'bdr', 'shd', 'fitText', 'vertAlign', 'rtl', 'cs', 'em', 'lang', 'eastAsianLayout',
  'specVanish', 'oMath'];
// Unknown/trailing elements (e.g. rPrChange) sort last, so markup lands before them.
export const rPrIdx = (local: string | null): number => {
  const i = RPR_ORDER.indexOf(local || '');
  return i < 0 ? RPR_ORDER.length : i;
};

/** Insert `el` into `rPr` at its CT_RPr-ordered position. */
function insertInRPrOrder(rPr: Element, el: Element): void {
  const idx = rPrIdx(el.localName);
  for (const c of Array.from(rPr.childNodes)) {
    const ce = c as Element;
    if (ce.nodeType === 1 && rPrIdx(ce.localName) > idx) { rPr.insertBefore(el, ce); return; }
  }
  rPr.appendChild(el);
}

/** Remove the given child elements from an rPr and insert fresh markup elements in order. */
function markRPr(rPrXml: string, removeLocal: string[], add: MarkEl[]): string {
  const rPr = parseEl(rPrXml || '<w:rPr/>');
  const doc = rPr.ownerDocument!;
  for (const local of removeLocal) {
    for (const e of Array.from(rPr.getElementsByTagNameNS(W_NS, local))) e.parentNode && e.parentNode.removeChild(e);
  }
  for (const a of add) {
    const el = makeEl(doc, 'w:' + a.tag);
    for (const [k, v] of Object.entries(a.attrs || {})) el.setAttribute(k, v);
    insertInRPrOrder(rPr, el);
  }
  return serializeClean(rPr);
}

export function addDeleteMarkup(rPrXml: string): string {
  return markRPr(rPrXml, ['color', 'strike', 'dstrike'],
    [{ tag: 'strike' }, { tag: 'color', attrs: { 'w:val': 'C41E3A' } }]);
}

export function addInsertMarkup(rPrXml: string): string {
  return markRPr(rPrXml, ['color', 'u'],
    [{ tag: 'u', attrs: { 'w:val': 'double' } }, { tag: 'color', attrs: { 'w:val': '1A4B8C' } }]);
}

export function addMoveFromMarkup(rPrXml: string): string {
  return markRPr(rPrXml, ['color', 'strike', 'dstrike'],
    [{ tag: 'dstrike' }, { tag: 'color', attrs: { 'w:val': '2D6A4F' } }]);
}

export function addMoveToMarkup(rPrXml: string): string {
  return markRPr(rPrXml, ['color', 'u'],
    [{ tag: 'u', attrs: { 'w:val': 'double' } }, { tag: 'color', attrs: { 'w:val': '2D6A4F' } }]);
}


/**
 * Stable-sort an rPr's children into CT_RPr schema order. Source documents often
 * carry misordered run properties (Word tolerates, stricter consumers don't);
 * runs REBUILT by the diff embed that rPr verbatim, so normalize it. Verbatim
 * source paragraphs are deliberately left byte-identical — this is only for runs
 * Fredline constructs.
 */
export function normalizeRPrOrder(rPrXml: string): string {
  if (!rPrXml || rPrXml.indexOf('<w:rPr') === -1) return rPrXml;
  try {
    const rPr = parseEl(rPrXml);
    const kids = Array.from(rPr.childNodes).filter((c) => (c as Element).nodeType === 1) as Element[];
    if (kids.length < 2) return rPrXml;
    const sorted = kids.map((el, i) => ({ el, i }))
      .sort((a, b) => (rPrIdx(a.el.localName) - rPrIdx(b.el.localName)) || (a.i - b.i));
    let changed = false;
    for (let i = 0; i < kids.length; i++) if (sorted[i].el !== kids[i]) { changed = true; break; }
    if (!changed) return rPrXml;
    for (const { el } of sorted) rPr.appendChild(el); // re-append moves in order
    return serializeClean(rPr);
  } catch {
    return rPrXml;
  }
}

export function applyMarkupToAllRuns(rawXml: string, markupFn: (rPrXml: string) => string): string {
  return applyMarkupToAllRuns_node(rawXml, markupFn);
}

function applyMarkupToAllRuns_node(rawXml: string, markupFn: (rPrXml: string) => string): string {
  const p = parseEl(rawXml);
  const doc = p.ownerDocument!;
  for (const run of Array.from(p.getElementsByTagNameNS(W_NS, 'r'))) {
    // the run's DIRECT rPr child (not a nested rPr inside an rPrChange)
    let rPr: Element | null = null;
    for (const c of Array.from(run.childNodes)) {
      const e = c as Element;
      if (e.nodeType === 1 && e.localName === 'rPr' && e.namespaceURI === W_NS) { rPr = e; break; }
    }
    const newRPr = doc.importNode(parseEl(markupFn(rPr ? serializeClean(rPr) : '')), true);
    if (rPr) run.replaceChild(newRPr, rPr);
    else run.insertBefore(newRPr, run.firstChild);
  }
  return serializeClean(p);
}
